// Listen at document level: the theme editor replaces the header element on
// every setting change, which would drop a listener bound to the element itself.
if (!window.lwrSplitOverflowBlocker) {
  window.lwrSplitOverflowBlocker = true;
  document.addEventListener(
    'overflowMinimum',
    (e) => {
      const header = e.target instanceof Element ? e.target.closest('#header-component') : null;
      if (header?.dataset.lwrSplit && window.matchMedia('(min-width:750px)').matches) {
        e.stopImmediatePropagation();
      }
    },
    true
  );

  // Horizon 3.2+ opens submenus via JS pointerenter instead of CSS :hover
  // (as in <= 3.1). Two situations leave the hover state pointing at the
  // wrong item:
  //   1. The browser doesn't fire pointerenter when the menu is rendered or
  //      replaced underneath a stationary pointer (section hydration does
  //      this right after page load), which leaves the first hover dead.
  //   2. While a submenu is open, the active link gets a full-width
  //      "safety box" overlay (diagonal-movement grace into the submenu).
  //      During fast sweeps that overlay covers the sibling items, and
  //      Horizon only re-targets after the pointer has been idle for 50ms —
  //      which reads as hover lag.
  // Reconcile on any pointer movement instead: find the item whose actual
  // link box contains the pointer (elementsFromPoint sees through the
  // overlay) and (re-)activate it immediately.
  // First-open fix: the submenu uses content-visibility:auto, so the heights
  // Horizon measures on the very first open after a page load (submenu height
  // and the full-open header height that sizes the white underlay) can be
  // stale, clipping the bottom of the tallest column. Recompute both values
  // from the rendered DOM and correct the CSS variables when they drift.
  // (Overflow-menu items use a different height calculation — excluded via
  // :not([slot="overflow"]), matching the CSS. Items can carry an empty
  // slot="" left behind by the split-menu self-heal below, so :not([slot])
  // would wrongly skip them.)
  const lwrSyncSubmenuHeight = () => {
    const headerEl = document.getElementById('header-component');
    if (!headerEl) return;
    const current = parseInt(headerEl.style.getPropertyValue('--submenu-height')) || 0;
    if (current <= 0) return;
    const expandedLink = headerEl.querySelector(
      '.menu-list__list-item:not([slot="overflow"]) > .menu-list__link[aria-expanded="true"]'
    );
    const sub = expandedLink?.parentElement.querySelector('[ref="submenu[]"]');
    const real = sub ? sub.offsetHeight : 0;
    if (real <= 0) return;
    const isOverlap = headerEl.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisible =
      (isOverlap ? headerEl.querySelector('.header__row--top')?.offsetHeight : headerEl.offsetHeight) || 0;
    const fullOpen = real + headerVisible;
    const currentFull = parseInt(headerEl.style.getPropertyValue('--full-open-header-height')) || 0;
    if (Math.abs(real - current) > 1) {
      headerEl.style.setProperty('--submenu-height', `${real}px`);
    }
    if (headerVisible > 0 && Math.abs(fullOpen - currentFull) > 1) {
      headerEl.style.setProperty('--full-open-header-height', `${fullOpen}px`);
    }
  };

  // Re-check after every submenu activation, even when the mouse then sits
  // still: late layout of content-visibility content, web fonts, or images can
  // settle the real height a few frames after Horizon measured it. The
  // ResizeObserver catches submenu box changes; the timeouts catch drift that
  // never changes the box.
  const lwrObservedSubs = new WeakSet();
  const lwrSubResizeObserver = new ResizeObserver(() => lwrSyncSubmenuHeight());
  document.addEventListener(
    'pointerenter',
    (e) => {
      if (!(e.target instanceof Element) || !e.target.classList?.contains('menu-list__list-item')) return;
      const sub = e.target.querySelector(':scope > [ref="submenu[]"]');
      if (sub && !lwrObservedSubs.has(sub)) {
        lwrObservedSubs.add(sub);
        lwrSubResizeObserver.observe(sub);
      }
      requestAnimationFrame(() => requestAnimationFrame(lwrSyncSubmenuHeight));
      setTimeout(lwrSyncSubmenuHeight, 150);
      setTimeout(lwrSyncSubmenuHeight, 450);
    },
    true
  );

  // Coalesce to one check per frame, always using the latest coordinates —
  // dropping later events instead would leave the final position of a fast
  // sweep unprocessed.
  let lwrHoverPending = null;
  document.addEventListener(
    'pointermove',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      if (!window.matchMedia('(min-width:750px)').matches) return;
      const alreadyScheduled = lwrHoverPending !== null;
      lwrHoverPending = {x: e.clientX, y: e.clientY};
      if (alreadyScheduled) return;
      requestAnimationFrame(() => {
        const {x, y} = lwrHoverPending;
        lwrHoverPending = null;
        const headerEl = document.getElementById('header-component');
        if (!headerEl) return;
        lwrSyncSubmenuHeight();
        const items = [];
        for (const el of document.elementsFromPoint(x, y)) {
          const cand = el.closest('.menu-list__list-item');
          if (cand && headerEl.contains(cand) && !items.includes(cand)) items.push(cand);
        }
        if (items.length === 0) return;
        // Prefer the item whose own link box contains the pointer — the first
        // hit may be the active item's safety-box overlay covering a sibling.
        // When the pointer is below the row (heading into the submenu) no link
        // box contains it, so the overlay's own item wins and the diagonal
        // grace is preserved.
        const li = items.find((cand) => {
          const r = cand.querySelector(':scope > .menu-list__link')?.getBoundingClientRect();
          return r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
        }) ?? items[0];
        const link = li.querySelector(':scope > .menu-list__link');
        if (!link) return;
        const isOpen = parseInt(headerEl.style.getPropertyValue('--submenu-height')) > 0;
        const isActive = link.getAttribute('aria-expanded') === 'true';
        // The LWR split menu renders two header-menu components (left/right of
        // the logo). Each one only deactivates its own previous item, so when
        // the pointer crosses from one side to the other the stale item keeps
        // its expanded state (and hover underline). Clear it explicitly.
        for (const open of headerEl.querySelectorAll('.menu-list__link[aria-expanded="true"]')) {
          const openLi = open.closest('.menu-list__list-item');
          if (openLi && openLi !== li && openLi.closest('header-menu') !== li.closest('header-menu')) {
            openLi.dispatchEvent(new PointerEvent('pointerleave', {bubbles: false}));
          }
        }
        if (link.getAttribute('aria-haspopup') === 'true') {
          if (isActive && isOpen) return;
          if (isActive) {
            // Stuck state: marked active but visually closed — reset before reopening.
            li.dispatchEvent(new PointerEvent('pointerleave', {bubbles: false}));
          }
          li.dispatchEvent(new PointerEvent('pointerenter', {bubbles: false}));
        } else if (isOpen || items.length > 1) {
          // Plain item (no submenu) under the pointer while another item's
          // submenu or overlay is active: switch to it immediately so the open
          // submenu closes and the hover state follows the pointer.
          li.dispatchEvent(new PointerEvent('pointerenter', {bubbles: false}));
        }
      });
    },
    {capture: true, passive: true}
  );

  // Theme editor only: editor re-renders inject the section dynamically, which
  // leaves overflow-list's declarative shadow <template> behind as a light-DOM
  // child. That ghost sibling shifts the nth-child split rules by one, blanking
  // the left nav and overflowing the right one. Horizon only removes it after
  // an item intersects the viewport — which never happens when the shifted CSS
  // hides every item — so remove it ourselves. The removal also triggers the
  // component's MutationObserver, causing a clean reflow with correct indices.
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const header = e.target.querySelector('#header-component');
      if (!header?.dataset.lwrSplit) return;
      for (const list of header.querySelectorAll('.lwr-split-nav overflow-list')) {
        list.querySelector(':scope > template[shadowrootmode]')?.remove();
      }
    });
  }

  // ===== LWR split-menu overflow → hamburger =====
  // In split mode the overflow-list mechanics fight the split CSS that
  // divides items around the logo: overflowing items get slotted away
  // silently (no "More", no recovery on widening). Setting disabled doesn't
  // help — reflows ignore it, and worse, :host([disabled]) removes the More
  // button's measuring box (overflow-list.css), so reflows compare item
  // positions against a zero rect and conclude EVERY item overflows. The
  // neutralization is CSS instead: nowrap !important on the shadow list (see
  // lwr-header.liquid) makes every measurement conclude "everything fits", so
  // the component never moves anything. Here we only undo stale state, then
  // measure the rendered items ourselves — if either side wraps or exceeds
  // its column, flip the header to the hamburger via data-lwr-menu-overflow.
  // The menus are display:none in that state, so every measurement below is
  // taken with the flag (and the drawer menu style) removed again first.
  // A touch device is pinned to the drawer by Horizon's own setHeaderMenuStyle()
  // at every width, so there is nothing here for the split check to decide.
  const lwrIsTouch = () => 'ontouchstart' in window && navigator.maxTouchPoints > 0;

  // Rather than restating Horizon's mobile CSS for the overflow state (the
  // hamburger, the five-column grid, hiding the search icon, the mobile border
  // widths — several of those rules live in stock files we may not edit), flip
  // the one attribute the whole theme already keys its mobile presentation off.
  // The header then renders between 750px and 1099px exactly as it does below
  // 750px, instead of a half-mobile state that has to be maintained by hand.
  const lwrSetMenuStyle = (headerEl, overflowing) => {
    if (lwrIsTouch()) return;
    headerEl.dataset.menuStyle = overflowing ? 'drawer' : 'menu';
  };

  // Horizon recomputes data-menu-style on its own debounced resize listener
  // (header-menu.js, 100ms), which lands after ours and would hand the row back
  // to the desktop menu that does not fit. Re-assert rather than race it:
  // MutationObserver callbacks run as microtasks, before paint, so the
  // correction is never visible.
  new MutationObserver(() => {
    const headerEl = document.getElementById('header-component');
    if (!headerEl || lwrIsTouch()) return;
    if (headerEl.hasAttribute('data-lwr-menu-overflow') && headerEl.dataset.menuStyle !== 'drawer') {
      headerEl.dataset.menuStyle = 'drawer';
    }
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['data-menu-style'] });

  const lwrSplitOverflowCheck = () => {
    const headerEl = document.getElementById('header-component');
    if (!headerEl) return;
    if (!headerEl.dataset.lwrSplit) {
      headerEl.removeAttribute('data-lwr-menu-overflow');
      return;
    }
    if (lwrIsTouch() || !window.matchMedia('(min-width:750px)').matches) {
      if (headerEl.hasAttribute('data-lwr-menu-overflow')) {
        headerEl.removeAttribute('data-lwr-menu-overflow');
        lwrSetMenuStyle(headerEl, false);
      }
      return;
    }
    const wasOverflowing = headerEl.hasAttribute('data-lwr-menu-overflow');
    if (wasOverflowing) {
      // Both have to go before a single rect is read: with the drawer style
      // still on, the menus are display:none and would measure as zero wide, so
      // every check would conclude they fit and flip straight back.
      headerEl.removeAttribute('data-lwr-menu-overflow');
      lwrSetMenuStyle(headerEl, false);
    }
    const centerRect = headerEl.querySelector('.header__column--center')?.getBoundingClientRect();
    const leftRect = headerEl.querySelector('.header__column--left')?.getBoundingClientRect();
    const rightRect = headerEl.querySelector('.header__column--right')?.getBoundingClientRect();
    let overflowing = window.matchMedia('(max-width:1099px)').matches || Boolean(
      centerRect &&
        ((leftRect && leftRect.right > centerRect.left - 12) ||
          (rightRect && rightRect.left < centerRect.right + 12))
    );
    for (const list of headerEl.querySelectorAll('.lwr-split-nav overflow-list')) {
      if (overflowing) break;
      // Must NOT be disabled: :host([disabled]) breaks the More measuring box.
      list.removeAttribute('disabled');
      // Self-heal: items already slotted into the hidden More dropdown stay
      // there until the next component reflow — pull them back into the row.
      // Remove the attribute outright: assigning li.slot = '' leaves slot=""
      // behind, and an item with any slot attribute no longer matches the
      // :not([slot]) selectors used elsewhere (this broke the first-open
      // submenu height sync above for every item this loop had touched).
      for (const li of list.querySelectorAll(':scope > .menu-list__list-item[slot="overflow"]')) {
        li.removeAttribute('slot');
      }
      const colRect = list.closest('.header__column')?.getBoundingClientRect();
      const items = [...list.querySelectorAll('.menu-list__list-item')].filter(
        (li) => li.getBoundingClientRect().width > 0
      );
      if (!colRect || items.length === 0) continue;
      // Primary signal: the column itself got pushed outside the row. When
      // space runs out, the grid tracks (sized by the nowrap menus'
      // min-content) overflow the .header__columns wrapper — items stay
      // inside their own column rects, so per-item bounds checks can't see it.
      const wrapperRect = list.closest('.header__columns')?.getBoundingClientRect();
      if (wrapperRect && (colRect.right > wrapperRect.right + 4 || colRect.left < wrapperRect.left - 4)) {
        overflowing = true;
        break;
      }
      const firstTop = items[0].getBoundingClientRect().top;
      for (const li of items) {
        const r = li.getBoundingClientRect();
        // The list intentionally bleeds half a menu-gap outside its column,
        // so allow a tolerance.
        if (r.top > firstTop + 4 || r.right > colRect.right + 24 || r.left < colRect.left - 24) {
          overflowing = true;
          break;
        }
      }
      if (overflowing) break;
    }
    const changed = wasOverflowing !== overflowing;
    headerEl.toggleAttribute('data-lwr-menu-overflow', overflowing);
    lwrSetMenuStyle(headerEl, overflowing);
    // Flipping the hamburger changes the layout being measured (drawer icon
    // appears/disappears) — verify once more after it settles so a check that
    // ran mid-relayout can't leave a stale state behind. The pending flag
    // keeps re-checks from piling up.
    if (changed && !lwrSplitRecheckPending) {
      lwrSplitRecheckPending = true;
      setTimeout(() => {
        lwrSplitRecheckPending = false;
        lwrSplitOverflowCheck();
      }, 150);
    }
  };
  let lwrSplitRecheckPending = false;

  let lwrSplitCheckScheduled = false;
  const lwrScheduleSplitCheck = () => {
    if (lwrSplitCheckScheduled) return;
    lwrSplitCheckScheduled = true;
    const run = () => {
      if (!lwrSplitCheckScheduled) return;
      lwrSplitCheckScheduled = false;
      lwrSplitOverflowCheck();
    };
    requestAnimationFrame(run);
    // rAF callbacks don't run while the page is hidden (background tab,
    // editor preview offscreen) — without a fallback, one resize while hidden
    // would latch the scheduled flag and kill every future check until the
    // page is visible again. Whichever fires first wins; `run` self-guards.
    setTimeout(run, 300);
  };
  window.addEventListener('resize', lwrScheduleSplitCheck);
  document.addEventListener('shopify:section:load', lwrScheduleSplitCheck);
  // Initial passes: layout, fonts, and section hydration all settle at
  // different times after load.
  lwrScheduleSplitCheck();
  setTimeout(lwrSplitOverflowCheck, 300);
  setTimeout(lwrSplitOverflowCheck, 1200);
  setTimeout(lwrSplitOverflowCheck, 2500);

  // ===== LWR mobile drawer (Gymshark-style) =====
  // All interactions via delegation so theme-editor re-renders stay wired.
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    const drawerEl = e.target.closest('.lwr-mdrawer');
    if (!drawerEl) return;

    // Click on the details element itself = the ::before backdrop (desktop
    // side-panel mode) — close the drawer.
    if (e.target === drawerEl) {
      drawerEl.removeAttribute('open');
      return;
    }

    const tab = e.target.closest('[data-lwr-tab]');
    if (tab) {
      for (const el of drawerEl.querySelectorAll('[data-lwr-tab]')) {
        el.classList.toggle('is-active', el === tab);
      }
      for (const panel of drawerEl.querySelectorAll('[data-lwr-panel]')) {
        panel.classList.toggle('is-active', panel.dataset.lwrPanel === tab.dataset.lwrTab);
      }
      tab.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'});
      return;
    }

    const drill = e.target.closest('[data-lwr-drill]');
    if (drill) {
      drawerEl.querySelector(`[data-lwr-sub="${drill.dataset.lwrDrill}"]`)?.classList.add('is-open');
      return;
    }

    const back = e.target.closest('[data-lwr-back]');
    if (back) {
      back.closest('[data-lwr-sub]')?.classList.remove('is-open');
      return;
    }

    if (e.target.closest('[data-lwr-drawer-close]')) {
      drawerEl.removeAttribute('open');
      return;
    }

    if (e.target.closest('[data-lwr-drawer-search]')) {
      drawerEl.removeAttribute('open');
      document.querySelector('header-component .search-action button')?.click();
    }
  });

  // Scroll lock + reset drill state on open/close ('toggle' doesn't bubble,
  // so listen in the capture phase).
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!(d instanceof HTMLElement) || !d.classList.contains('lwr-mdrawer')) return;
      document.documentElement.classList.toggle('lwr-mdrawer-open', d.hasAttribute('open'));
      if (!d.hasAttribute('open')) {
        for (const sub of d.querySelectorAll('[data-lwr-sub].is-open')) sub.classList.remove('is-open');
      }
    },
    true
  );

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelector('details.lwr-mdrawer[open]')?.removeAttribute('open');
  });
}
